import { filter, includes, map, uniq } from 'lodash';
import { stringify } from 'qs';
import { ArrayParam, BooleanParam, encodeQueryParams, JsonParam, NumberParam, StringParam } from 'use-query-params';

import { getDefaultManifestDialogUrlState, ManifestDialogUrlState } from './dialogState';
import { getManifestDialogUrl } from './manifestDialogUrl';
import {
  OrganizationDatasetMode,
  OrganizationEntryContext,
  OrganizationEntryMode,
  OrganizationEntryOrigin,
  OrganizationReturnDescriptor,
} from './organizationTypes';

const origins: OrganizationEntryOrigin[] = ['dataset', 'uploads', 'lostAndFound'];
const datasetModes: OrganizationDatasetMode[] = ['cases', 'slides', 'needsAssignment'];
const entryModes: OrganizationEntryMode[] = ['standard', 'advanced'];
const uploadFilters = ['awaitingIngest', 'missingCaseAssignment', 'assignedToCase', 'manifest', 'other', 'hidden'];
const missingReasons = ['missingIngest', 'missingCaseAssignment'];

const safeString = (value: unknown, maxLength = 200) =>
  typeof value === 'string' && value.length > 0 && value.length <= maxLength ? value : undefined;

const safeStringArray = (value: unknown, allowlist?: string[]) => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const values = uniq(
    filter(
      map(value, (item) => safeString(item)),
      (item): item is string => Boolean(item) && (!allowlist || includes(allowlist, item))
    )
  );
  return values.length ? values.slice(0, 50) : undefined;
};

const safePositiveInteger = (value: unknown) =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : undefined;

export const sanitizeOrganizationReturnDescriptor = (value: unknown): OrganizationReturnDescriptor | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as Partial<OrganizationReturnDescriptor>;
  const origin = includes(origins, candidate.origin) ? candidate.origin : undefined;
  const labId = safeString(candidate.labId);
  const studyId = safeString(candidate.studyId);
  if (!origin || !labId || !studyId) {
    return undefined;
  }

  const datasetMode = includes(datasetModes, candidate.datasetMode) ? candidate.datasetMode : undefined;
  return {
    origin,
    labId,
    studyId,
    ...(datasetMode ? { datasetMode } : {}),
    ...(safePositiveInteger(candidate.page) ? { page: safePositiveInteger(candidate.page) } : {}),
    ...(safePositiveInteger(candidate.pageSize) ? { pageSize: safePositiveInteger(candidate.pageSize) } : {}),
    ...(safeString(candidate.searchTerm) ? { searchTerm: safeString(candidate.searchTerm) } : {}),
    ...(safeStringArray(candidate.uploadFilters, uploadFilters)
      ? { uploadFilters: safeStringArray(candidate.uploadFilters, uploadFilters) }
      : {}),
    ...(safeStringArray(candidate.missingReasons, missingReasons)
      ? { missingReasons: safeStringArray(candidate.missingReasons, missingReasons) }
      : {}),
    ...(safeStringArray(candidate.sourceStudyIds) ? { sourceStudyIds: safeStringArray(candidate.sourceStudyIds) } : {}),
    ...(safeString(candidate.uploadedAtStart) ? { uploadedAtStart: safeString(candidate.uploadedAtStart) } : {}),
    ...(safeString(candidate.uploadedAtEnd) ? { uploadedAtEnd: safeString(candidate.uploadedAtEnd) } : {}),
    ...(safeString(candidate.anchor, 300) ? { anchor: safeString(candidate.anchor, 300) } : {}),
  };
};

export const buildOrganizationEntryContext = ({
  mode = 'standard',
  returnTo,
}: {
  mode?: OrganizationEntryMode;
  returnTo: OrganizationReturnDescriptor;
}): OrganizationEntryContext | undefined => {
  const safeReturnTo = sanitizeOrganizationReturnDescriptor(returnTo);
  return safeReturnTo && includes(entryModes, mode) ? { mode, returnTo: safeReturnTo } : undefined;
};

export const buildOrganizationManifestState = ({
  manifestState,
  mode = 'standard',
  returnTo,
}: {
  manifestState?: ManifestDialogUrlState;
  mode?: OrganizationEntryMode;
  returnTo: OrganizationReturnDescriptor;
}): ManifestDialogUrlState => ({
  ...getDefaultManifestDialogUrlState(),
  ...(manifestState || {}),
  organizationEntry: buildOrganizationEntryContext({
    mode,
    returnTo,
  }),
});

export const getOrganizationEntryUrl = ({
  studyId,
  labId,
  manifestState,
  mode,
  returnTo,
}: {
  studyId: string;
  labId: string;
  manifestState?: ManifestDialogUrlState;
  mode?: OrganizationEntryMode;
  returnTo: OrganizationReturnDescriptor;
}) =>
  getManifestDialogUrl({
    studyId,
    labId,
    manifestState: buildOrganizationManifestState({ manifestState, mode, returnTo }),
  });

export const getOrganizationReturnUrl = ({
  entry,
  fallbackLabId,
  fallbackStudyId,
  accessibleOrigins,
}: {
  entry?: OrganizationEntryContext;
  fallbackLabId: string;
  fallbackStudyId: string;
  accessibleOrigins: OrganizationEntryOrigin[];
}) => {
  const returnTo = sanitizeOrganizationReturnDescriptor(entry?.returnTo);
  const fallback = `/procedures?${stringify(
    encodeQueryParams(
      { filters: JsonParam, labId: StringParam },
      { filters: { studyId: fallbackStudyId }, labId: fallbackLabId }
    )
  )}`;

  if (
    !returnTo ||
    returnTo.labId !== fallbackLabId ||
    returnTo.studyId !== fallbackStudyId ||
    !includes(accessibleOrigins, returnTo.origin)
  ) {
    return { url: fallback, usedFallback: Boolean(returnTo) };
  }

  if (returnTo.origin === 'dataset') {
    const mode = returnTo.datasetMode || 'slides';
    return {
      url: `/procedures?${stringify(
        encodeQueryParams(
          {
            filters: JsonParam,
            labId: StringParam,
            page: NumberParam,
            searchTerm: StringParam,
            slidesMode: BooleanParam,
            pendingSlidesMode: BooleanParam,
            anchor: StringParam,
          },
          {
            filters: { studyId: returnTo.studyId },
            labId: returnTo.labId,
            page: returnTo.page,
            searchTerm: returnTo.searchTerm,
            slidesMode: mode !== 'cases',
            pendingSlidesMode: mode === 'needsAssignment',
            anchor: returnTo.anchor,
          }
        )
      )}`,
      usedFallback: false,
    };
  }

  const pathname = returnTo.origin === 'uploads' ? '/uploads-dashboard' : '/lost-and-found';
  return {
    url: `${pathname}?${stringify(
      encodeQueryParams(
        {
          filters: JsonParam,
          labId: StringParam,
          page: NumberParam,
          pageSize: NumberParam,
          searchQuery: StringParam,
          uploadedAtStart: StringParam,
          uploadedAtEnd: StringParam,
          anchor: StringParam,
          uploadFilters: ArrayParam,
          missingReasons: ArrayParam,
          sourceStudyIds: ArrayParam,
        },
        {
          filters: { studyId: returnTo.studyId },
          labId: returnTo.labId,
          page: returnTo.page,
          pageSize: returnTo.pageSize,
          searchQuery: returnTo.searchTerm,
          uploadedAtStart: returnTo.uploadedAtStart,
          uploadedAtEnd: returnTo.uploadedAtEnd,
          anchor: returnTo.anchor,
          uploadFilters: returnTo.uploadFilters,
          missingReasons: returnTo.missingReasons,
          sourceStudyIds: returnTo.sourceStudyIds,
        }
      ),
      { arrayFormat: 'repeat' }
    )}`,
    usedFallback: false,
  };
};
